<template>
  <div>
    <v-card outlined>
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            sm="12"
            md="12"
            lg="3"
            xl="3"
          >
            <v-select
              :disabled="updatingChannel || updatingPeriod"
              :loading="updatingPeriod"
              class="white"
              outlined
              hide-details
              dense
              :label="$t('period')"
              :items="periodOptions"
              item-value="key"
              item-text="label"
              v-model="period"
            >
              <template v-slot:selection="{ item }">
                <span v-if="item.key === 'custom'">
                  <span v-if="customTimeAndDateFormatted()">
                    {{ customTimeAndDateFormatted() }}
                  </span>
                  <span v-else>
                    {{ item.label }}
                  </span>
                </span>
                <span v-else>
                  {{ item.label }}
                </span>
              </template>
              <template v-slot:append-outer>
                <v-tooltip
                  v-if="period === 'custom'"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      @click="showDashDateDialog = true"
                      v-on="on"
                      v-bind="attrs"
                    >
                      mdi-calendar
                    </v-icon>
                  </template>
                  <span>
                    {{ $t('changeCustomPeriod') }}
                  </span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>
          <v-col
            cols="12"
            sm="12"
            md="12"
            lg="3"
            xl="3"
          >
            <v-select
              multiple
              :disabled="updatingChannel || updatingPeriod"
              :loading="updatingChannel"
              class="white"
              outlined
              hide-details
              dense
              chips
              small-chips
              deletable-chips
              :placeholder="$t('all')"
              persistent-placeholder
              :label="$t('channels')"
              :items="channels"
              item-value="uuid"
              item-text="label"
              v-model="channel"
            >
              <template v-slot:item="data">
                <div
                  class="d-flex justify-start align-center"
                  style="width: 100%"
                >
                  <!-- Checkbox Indicator -->
                  <v-checkbox
                    :input-value="channel.includes(data.item.uuid)"
                    :disabled="true"
                    class="shrink mr-2"
                  ></v-checkbox>
                  <span class="flex-grow-1">{{ data.item.label }}</span>
                  <v-chip
                    v-if="data.item.label !== 'All' && !data.item.active"
                    small
                    outlined
                    class="ml-2"
                    >Disabled</v-chip
                  >
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg
          >
            <TimeSelectorV2
              initial-time="00:00"
              label="Start Time"
              :dense="true"
              combobox-class="white"
              @selectTime="selectDayStartsAt"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg
          >
            <TimeSelectorV2
              initial-time="23:59"
              label="End Time"
              :dense="true"
              combobox-class="white"
              @selectTime="selectDayEndsAt"
            />
          </v-col>
          <v-col
            cols="12"
            sm="12"
            md="12"
            lg="3"
            xl="3"
            class="text-end"
          >
            <div>
              <v-btn
                color="#67AD5B"
                class="white--text"
                depressed
                @click="showScheduledReportsOverlay"
              >
                {{ $t('scheduledReports') }}
              </v-btn>
            </div>
            <v-btn
              color="#67AD5B"
              class="white--text mt-2"
              depressed
              @click="showSendDashReportDialog"
            >
              {{ $t('send') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <draggable
      class="row"
      :list="dashes"
      :move="positionChanged"
      :options="draggableOptions"
    >
      <template v-for="(dash, i) in dashes">
        <v-col
          class="draggable pb-0"
          :key="dash.uuid"
          :data-dash-uuid="dash.uuid"
          cols="12"
          :md="dash.cols"
          sm="12"
        >
          <DashTable
            v-if="collection.dashes[i].renderType === 'table'"
            :collection="collection"
            v-model="collection.dashes[i]"
            :first="i === 0"
            :last="i === dashes.length - 1"
            @decreaseOrder="decreaseOrder(i, collection.dashes[i])"
            @increaseOrder="increaseOrder(i, collection.dashes[i])"
          />
          <DashChart
            v-if="collection.dashes[i].renderType === 'gchart'"
            :collection="collection"
            v-model="collection.dashes[i]"
            :first="i === 0"
            :last="i === dashes.length - 1"
            @decreaseOrder="decreaseOrder(i, collection.dashes[i])"
            @increaseOrder="increaseOrder(i, collection.dashes[i])"
          />
          <DashSingleValue
            v-if="collection.dashes[i].renderType === 'singleValue'"
            :collection="collection"
            v-model="collection.dashes[i]"
            :first="i === 0"
            :last="i === dashes.length - 1"
            @decreaseOrder="decreaseOrder(i, collection.dashes[i])"
            @increaseOrder="increaseOrder(i, collection.dashes[i])"
          />
        </v-col>
      </template>
    </draggable>
    <CustomDashDateRange
      v-if="showDashDateDialog"
      :collection="collection"
      :start-time-in-millis="collection.startTimeInMillis"
      :end-time-in-millis="collection.endTimeInMillis"
      @onConfirm="updateTimeAndDateRange"
      @onCancel="showDashDateDialog = !showDashDateDialog"
    />
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import TimeSelectorV2 from '@/components/common/TimeSelectorV2'

export default {
  name: 'DashCollection',
  props: {
    collection: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    DashSingleValue: () => import('@/components/dash/DashSingleValue'),
    DashChart: () => import('@/components/dash/DashChart'),
    DashTable: () => import('@/components/dash/DashTable'),
    Draggable,
    CustomDashDateRange: () => import('@/components/dash/CustomDashDateRange'),
    TimeSelectorV2
  },
  data: () => ({
    draggableOptions: {
      draggable: '.draggable',
      handle: '.draggable-item'
    },
    showNotice: false,
    updatingChannel: false,
    updatingPeriod: false,
    showCustomTimeInterval: false,
    showDashDateDialog: false,
    oneMinuteInMillis: 60000,
    oneHourInMillis: 3600000,
    dayStartsAtInMillis: 0, // 00:00 AM in millis
    dayEndsAtInMillis: 86340000 // 23:59 PM in millis
  }),
  watch: {
    period(newValue) {
      if (newValue === 'custom') {
        this.showDashDateDialog = true
      }
    },
    dayStartsAtInMillis() {
      this.updateTimeOfDayRange()
    },
    dayEndsAtInMillis() {
      this.updateTimeOfDayRange()
    }
  },
  methods: {
    showSendDashReportDialog() {
      this.$store.commit('updateGenericDialog', {
        component: 'components/dash/SendDashReportForm',
        props: {
          dashCollection: this.collection,
          startTimeInMillis: this.startTimeInMillis,
          endTimeInMillis: this.endTimeInMillis
        }
      })
    },
    updateTimeAndDateRange(timeRange) {
      this.showDashDateDialog = !this.showDashDateDialog
      this.updatingPeriod = true

      const timeIntervalFrom = this.convertMillisToHoursMinutes(this.dayStartsAtInMillis)
      const timeIntervalTo = this.convertMillisToHoursMinutes(this.dayEndsAtInMillis)

      const payloadObject = {
        uuid: this.collection.uuid,
        name: this.collection.name,
        data: JSON.stringify(this.collection.data),
        period: this.period,
        startTimeInMillis: null,
        endTimeInMillis: null,
        timeIntervalFrom,
        timeIntervalTo
      }

      if (
        timeRange.startTimeInMillis !== null &&
        timeRange.startTimeInMillis !== undefined &&
        timeRange.endTimeInMillis !== null &&
        timeRange.endTimeInMillis !== undefined
      ) {
        payloadObject.startTimeInMillis = timeRange.startTimeInMillis
        payloadObject.endTimeInMillis = timeRange.endTimeInMillis
        payloadObject.period = 'custom'

        // We must update startTimeInMillis and endTimeInMillis in store so that the dashes themselves fetch data correctly
        this.$store.commit('updateStartTimeInMillis', timeRange.startTimeInMillis)
        this.$store.commit('updateEndTimeInMillis', timeRange.endTimeInMillis)
      }

      this.$store
        .dispatch('updateDashCollection', payloadObject)
        .then((updatedDashCollection) => {
          this.$store.commit('updateDashCollection', updatedDashCollection)
        })
        .finally(() => {
          this.updatingPeriod = false
        })
    },
    positionChanged(e) {
      // ATTN. It is possible to use @change rather than :move to handle moving dash card on drop rather on move
      const list = e.relatedContext.list
      const elementIndex = e.draggedContext.index
      const element = e.draggedContext.element
      const futureIndex = e.draggedContext.futureIndex
      const newList = list.slice(0, elementIndex).concat(list.slice(elementIndex + 1))

      newList.splice(futureIndex, 0, element)

      this.$store.dispatch(
        'updateDashOrder',
        newList.map((listItem, index) => {
          return {
            uuid: listItem.uuid,
            weight: index
          }
        })
      )

      // SINGLE ELEMENT UPDATE
      // this.$store.dispatch('updateDash', {
      // 	uuid: e.draggedContext.element.uuid,
      // 	weight: e.draggedContext.futureIndex
      // })
    },
    decreaseOrder(index, element) {
      const list = this.dashes
      const oldIndex = index
      const newIndex = index - 1
      const newList = list.slice(0, oldIndex).concat(list.slice(oldIndex + 1))

      newList.splice(newIndex, 0, element)

      this.$store
        .dispatch(
          'updateDashOrder',
          newList.map((listItem, innerIndex) => {
            return {
              uuid: listItem.uuid,
              weight: innerIndex
            }
          })
        )
        .then(() => {
          this.$store.dispatch('getDashCollections')
        })
    },
    increaseOrder(index, element) {
      const list = this.dashes
      const oldIndex = index
      const newIndex = index + 1
      const newList = list.slice(0, oldIndex).concat(list.slice(oldIndex + 1))

      newList.splice(newIndex, 0, element)

      this.$store
        .dispatch(
          'updateDashOrder',
          newList.map((listItem, innerIndex) => {
            return {
              uuid: listItem.uuid,
              weight: innerIndex
            }
          })
        )
        .then(() => {
          this.$store.dispatch('getDashCollections')
        })
    },
    closeNotice() {
      this.showNotice = false
    },
    customTimeAndDateFormatted() {
      if (
        this.collection.startTimeInMillis !== null &&
        this.collection.startTimeInMillis !== undefined &&
        this.collection.endTimeInMillis !== null &&
        this.collection.endTimeInMillis !== undefined
      ) {
        return `From ${this.$moment(this.collection.startTimeInMillis).format('DD-MM-YYYY')} to ${this.$moment(this.collection.endTimeInMillis).format('DD-MM-YYYY')}`
      }
      return ''
    },
    showScheduledReportsOverlay() {
      this.$store.commit('updateContextOverlay', {
        component: 'components/dash/ScheduledReportsCard',
        props: {
          dashCollection: this.collection
        }
      })
    },
    selectDayStartsAt(value) {
      this.dayStartsAtInMillis = this.convertHoursMinutesToMillis(value)
    },
    selectDayEndsAt(value) {
      this.dayEndsAtInMillis = this.convertHoursMinutesToMillis(value)
    },
    updateTimeOfDayRange() {
      this.updatingPeriod = true

      if (this.dayEndsAtInMillis <= this.dayStartsAtInMillis) {
        this.dayEndsAtInMillis = this.dayStartsAtInMillis + this.oneMinuteInMillis
      }

      const timeIntervalFrom = this.convertMillisToHoursMinutes(this.dayStartsAtInMillis)
      const timeIntervalTo = this.convertMillisToHoursMinutes(this.dayEndsAtInMillis)

      const payload = {
        uuid: this.collection.uuid,
        name: this.collection.name,
        data: JSON.stringify(this.collection.data),
        endTimeInMillis: this.collection.endTimeInMillis,
        startTimeInMillis: this.collection.startTimeInMillis,
        timeIntervalFrom,
        timeIntervalTo
      }

      this.$store
        .dispatch('updateDashCollection', payload)
        .then((updatedDashCollection) => {
          this.$store.commit('updateDashCollection', updatedDashCollection)
        })
        .finally(() => {
          this.updatingPeriod = false
        })
    },
    convertMillisToHoursMinutes(value) {
      const hours = Math.floor(value / this.oneHourInMillis)
      const minutes = Math.floor((value % this.oneHourInMillis) / this.oneMinuteInMillis)
      const result = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      return result
    },
    convertHoursMinutesToMillis(value) {
      if (!value || typeof value !== 'string' || !value.match(/^\d{1,2}:\d{2}$/)) {
        console.error(`Invalid time format: ${value}. Expected "HH:mm".`)
        return 0
      }
      const hoursInNumberFormat = +value.split(':')[0]
      const minutesInNumberFormat = +value.split(':')[1]
      if (hoursInNumberFormat < 0 || hoursInNumberFormat > 23 || minutesInNumberFormat < 0 || minutesInNumberFormat > 59) {
        console.error(`Time values out of range: ${value}`)
        return 0
      }
      const hoursInMillis = hoursInNumberFormat * this.oneHourInMillis
      const minutesInMillis = minutesInNumberFormat * this.oneMinuteInMillis
      return hoursInMillis + minutesInMillis
    }
  },
  computed: {
    startTimeInMillis() {
      let startTimeInMillis = 0
      const date = new Date()

      switch (this.collection.period) {
        case 'today':
          date.setUTCHours(0)
          date.setUTCMinutes(0)
          date.setUTCMinutes(0)
          date.setUTCMilliseconds(0)
          startTimeInMillis = date.getTime()
          break
        case 'yesterday':
          date.setUTCDate(date.getUTCDate() - 1)
          date.setUTCHours(0)
          date.setUTCMinutes(0)
          date.setUTCSeconds(0)
          date.setUTCMilliseconds(0)
          startTimeInMillis = date.getTime()
          break
        case 'current_week':
          date.setUTCDate(1)
          startTimeInMillis = this.$moment().startOf('isoweek').valueOf()
          break
        case 'previous_month':
          date.setUTCMonth(date.getUTCMonth() - 1)
          startTimeInMillis = this.$moment().subtract(1, 'months').startOf('month').valueOf()
          break
        case 'previous_week':
          date.setUTCDate(2)
          startTimeInMillis = this.$moment().subtract(1, 'week').startOf('isoweek').valueOf()
          break
        case 'custom':
          startTimeInMillis = this.collection.startTimeInMillis
          break
        default:
          startTimeInMillis = this.$moment().startOf('month').valueOf()
      }

      return startTimeInMillis
    },
    endTimeInMillis() {
      let endTimeInMillis = 0
      const date = new Date()
      switch (this.collection.period) {
        case 'today':
          date.setUTCHours(23)
          date.setUTCMinutes(59)
          date.setUTCMilliseconds(59)
          date.setUTCSeconds(59)
          endTimeInMillis = date.getTime()
          break
        case 'yesterday':
          date.setUTCDate(date.getUTCDate() - 1)
          date.setUTCHours(23)
          date.setUTCMinutes(59)
          date.setUTCSeconds(59)
          date.setUTCMilliseconds(59)
          endTimeInMillis = date.getTime()
          break
        case 'current_week':
          date.setUTCDate(1)
          endTimeInMillis = this.$moment().endOf('isoweek').valueOf()
          break
        case 'previous_month':
          date.setUTCMonth(date.getMonth() - 1)
          endTimeInMillis = this.$moment().subtract(1, 'months').endOf('month').valueOf()
          break
        case 'previous_week':
          date.setUTCDate(2)
          endTimeInMillis = this.$moment().subtract(1, 'week').endOf('isoweek').valueOf()
          break
        case 'custom':
          endTimeInMillis = this.collection.endTimeInMillis
          break
        default:
          endTimeInMillis = this.$moment().endOf('month').valueOf()
      }

      return endTimeInMillis
    },
    dashes() {
      return this.collection.dashes
    },
    periodOptions() {
      return [
        {
          label: this.$t('today'),
          key: 'today'
        },
        {
          label: this.$t('yesterday'),
          key: 'yesterday'
        },
        {
          label: this.$t('currentMonth'),
          key: 'current_month'
        },
        {
          label: this.$t('currentWeek'),
          key: 'current_week'
        },
        {
          label: this.$t('previousMonth'),
          key: 'previous_month'
        },
        {
          label: this.$t('previousWeek'),
          key: 'previous_week'
        },
        {
          label: `${this.$t('custom')}${this.customTimeAndDateFormatted() ? ` ${this.$t('selectAgainToChange')}` : ''}`,
          key: 'custom'
        }
      ]
    },
    period: {
      set(value) {
        this.updatingPeriod = true
        const timeIntervalFrom = this.convertMillisToHoursMinutes(this.dayStartsAtInMillis)
        const timeIntervalTo = this.convertMillisToHoursMinutes(this.dayEndsAtInMillis)

        const payload = {
          uuid: this.collection.uuid,
          name: this.collection.name,
          data: JSON.stringify(this.collection.data),
          period: value,
          timeIntervalFrom,
          timeIntervalTo
        }

        this.$store
          .dispatch('updateDashCollection', payload)
          .then((updatedDashCollection) => {
            this.$store.commit('updateDashCollection', updatedDashCollection)
          })
          .finally(() => {
            this.updatingPeriod = false
          })
      },
      get() {
        return this.collection.period || 'current_month'
      }
    },
    channel: {
      set(value) {
        this.updatingChannel = true

        const timeIntervalFrom = this.convertMillisToHoursMinutes(this.dayStartsAtInMillis)
        const timeIntervalTo = this.convertMillisToHoursMinutes(this.dayEndsAtInMillis)
        const payload = {
          uuid: this.collection.uuid,
          name: this.collection.name,
          data: JSON.stringify(this.collection.data),
          endTimeInMillis: this.collection.endTimeInMillis,
          startTimeInMillis: this.collection.startTimeInMillis,
          timeIntervalFrom,
          timeIntervalTo,
          channels: value
        }

        this.$store
          .dispatch('updateDashCollection', payload)
          .then((updatedDashCollection) => {
            this.$store.commit('updateDashCollection', updatedDashCollection)
          })
          .finally(() => {
            this.updatingChannel = false
          })
      },
      get() {
        return this.collection.channels || ['']
      }
    },
    channels() {
      return this.$store.state.channels
    }
  }
}
</script>
